
import React from "react";
import { Modal, Descriptions, Tag, Divider, Card, Row, Col, Typography } from "antd";
import dayjs from "dayjs";
import {
  DollarOutlined,
  CalendarOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  FileProtectOutlined,
  ClockCircleOutlined,
  TagOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const ViewExpenseModal = ({ visible, onClose, expenseData }) => {
  // تحديد لون النوع بناءً على نوع المصروف
  const getExpenseTypeColor = (type) => {
    const colors = {
      "رسوم قضائية": { color: "#1890ff", bgColor: "#e6f7ff" },
      "أتعاب محاماة": { color: "#52c41a", bgColor: "#f6ffed" },
      "رسوم ترجمة": { color: "#722ed1", bgColor: "#f9f0ff" },
      "تكاليف سفر": { color: "#fa8c16", bgColor: "#fff7e6" },
      "طباعة وتصوير": { color: "#13c2c2", bgColor: "#e6fffb" },
      "تكاليف أخرى": { color: "#8c8c8c", bgColor: "#f5f5f5" },
    };
    return colors[type] || { color: "#8c8c8c", bgColor: "#f5f5f5" };
  };

  return (
    <Modal
      title={
        <div className="flex items-center justify-between gap-2" dir="rtl">
          <Title level={4} className="m-0 flex items-center gap-2">
            <FileProtectOutlined style={{ color: "#1890ff" }} />
            تفاصيل المصروف
          </Title>
          <Tag color="blue">#{expenseData?.id}</Tag>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      dir="rtl"
      className="view-expense-modal"
      styles={{ 
        body: { 
          maxHeight: "80vh", 
          overflow: "auto", 
          padding: "24px",
          direction: "rtl"
        } 
      }}
      destroyOnClose
    >
      {expenseData ? (
        <div className="flex flex-col gap-6" dir="rtl">
          {/* قسم معلومات المصروف الرئيسية */}
          <Card 
            className="expense-main-info" 
            bordered={false}
            style={{ 
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
              borderRadius: "8px"
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div className="flex items-start gap-3 mb-4">
                  <div 
                    className="icon-wrapper" 
                    style={{
                      backgroundColor: getExpenseTypeColor(expenseData.expense_type).bgColor,
                      width: "48px",
                      height: "48px",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <TagOutlined 
                      style={{ 
                        color: getExpenseTypeColor(expenseData.expense_type).color,
                        fontSize: "20px"
                      }} 
                    />
                  </div>
                  <div className="flex-1">
                    <Text type="secondary" className="block mb-1">نوع المصروف</Text>
                    <Tag 
                      color={getExpenseTypeColor(expenseData.expense_type).color}
                      style={{
                        fontSize: "15px",
                        padding: "4px 12px",
                        fontWeight: "500"
                      }}
                    >
                      {expenseData.expense_type}
                    </Tag>
                  </div>
                </div>
              </Col>
              
              <Col xs={24} sm={12}>
                <div className="flex items-start gap-3 mb-4">
                  <div 
                    className="icon-wrapper" 
                    style={{
                      backgroundColor: "#f0f5ff",
                      width: "48px",
                      height: "48px",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <DollarOutlined 
                      style={{ 
                        color: "#1890ff",
                        fontSize: "20px"
                      }} 
                    />
                  </div>
                  <div className="flex-1">
                    <Text type="secondary" className="block mb-1">المبلغ</Text>
                    <Text 
                      strong 
                      style={{
                        fontSize: "20px",
                        color: "#1890ff"
                      }}
                    >
                      {expenseData.amount?.toLocaleString("ar-IQ") || 0} د.ع
                    </Text>
                  </div>
                </div>
              </Col>
              
              <Col xs={24} sm={12}>
                <div className="flex items-start gap-3 mb-4">
                  <div 
                    className="icon-wrapper" 
                    style={{
                      backgroundColor: "#f6ffed",
                      width: "48px",
                      height: "48px",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <CalendarOutlined 
                      style={{ 
                        color: "#52c41a",
                        fontSize: "20px"
                      }} 
                    />
                  </div>
                  <div className="flex-1">
                    <Text type="secondary" className="block mb-1">تاريخ المصروف</Text>
                    <Text strong>
                      {expenseData.expense_date
                        ? dayjs(expenseData.expense_date).format("YYYY/MM/DD")
                        : "غير محدد"}
                    </Text>
                  </div>
                </div>
              </Col>
              
              {expenseData.receipt_number && (
                <Col xs={24} sm={12}>
                  <div className="flex items-start gap-3 mb-4">
                    <div 
                      className="icon-wrapper" 
                      style={{
                        backgroundColor: "#fff7e6",
                        width: "48px",
                        height: "48px",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <FileProtectOutlined 
                        style={{ 
                          color: "#fa8c16",
                          fontSize: "20px"
                        }} 
                      />
                    </div>
                    <div className="flex-1">
                      <Text type="secondary" className="block mb-1">رقم الإيصال</Text>
                      <Text strong>{expenseData.receipt_number}</Text>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </Card>
          
          {/* قسم الوصف والملاحظات */}
          <Card 
            title={
              <div className="flex items-center gap-2">
                <FileTextOutlined style={{ color: "#1890ff" }} />
                <span>التفاصيل</span>
              </div>
            }
            bordered={false}
            style={{ 
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
              borderRadius: "8px"
            }}
          >
            {expenseData.description && (
              <div className="mb-4" dir="rtl">
                <Text type="secondary" className="block mb-1">الوصف</Text>
                <div className="bg-gray-50 p-3 rounded" style={{ textAlign: "right" }}>
                  <Text>{expenseData.description}</Text>
                </div>
              </div>
            )}
            
            {expenseData.notes && (
              <div dir="rtl">
                <Text type="secondary" className="block mb-1">ملاحظات</Text>
                <div className="bg-gray-50 p-3 rounded" style={{ textAlign: "right" }}>
                  <Text>{expenseData.notes}</Text>
                </div>
              </div>
            )}
            
            {!expenseData.description && !expenseData.notes && (
              <div className="text-center py-4" dir="rtl">
                <Text type="secondary">لا توجد تفاصيل إضافية</Text>
              </div>
            )}
          </Card>
          
          {/* قسم معلومات النظام */}
          <Card 
            title={
              <div className="flex items-center gap-2">
                <InfoCircleOutlined style={{ color: "#1890ff" }} />
                <span>معلومات النظام</span>
              </div>
            }
            bordered={false}
            style={{ 
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
              borderRadius: "8px"
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div className="flex items-start gap-3" dir="rtl">
                  <div 
                    className="icon-wrapper" 
                    style={{
                      backgroundColor: "#f0f5ff",
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <ClockCircleOutlined 
                      style={{ 
                        color: "#1890ff",
                        fontSize: "18px"
                      }} 
                    />
                  </div>
                  <div className="flex-1">
                    <Text type="secondary" className="block mb-1">تاريخ الإضافة</Text>
                    <Text>
                      {expenseData.created_at
                        ? dayjs(expenseData.created_at).format("YYYY/MM/DD HH:mm")
                        : "غير محدد"}
                    </Text>
                  </div>
                </div>
              </Col>
              
              <Col xs={24} sm={12}>
                <div className="flex items-start gap-3" dir="rtl">
                  <div 
                    className="icon-wrapper" 
                    style={{
                      backgroundColor: "#f0f5ff",
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <ClockCircleOutlined 
                      style={{ 
                        color: "#1890ff",
                        fontSize: "18px"
                      }} 
                    />
                  </div>
                  <div className="flex-1">
                    <Text type="secondary" className="block mb-1">آخر تحديث</Text>
                    <Text>
                      {expenseData.updated_at
                        ? dayjs(expenseData.updated_at).format("YYYY/MM/DD HH:mm")
                        : dayjs(expenseData.created_at).format("YYYY/MM/DD HH:mm")}
                    </Text>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </div>
      ) : (
        <div className="text-center py-8" dir="rtl">
          <Text type="secondary">لا توجد بيانات متاحة</Text>
        </div>
      )}
    </Modal>
  );
};

export default ViewExpenseModal;
